import React, { useState } from 'react';

import { Trans } from 'react-i18next';

import { useCreateProjectPersonnelBulkDestructionMutation } from 'components/hooks/projectPersonnelBulkDestructionMutations';
import { useCreateProjectPersonnelBulkRestorationMutation } from 'components/hooks/projectPersonnelBulkRestorationMutations';
import { useProjectPersonnelsQuery } from 'components/hooks/projectPersonnelQueries';

import Paginator from 'components/application/Paginator';
import SuccessToast from 'components/application/SuccessToast';
import UndoableToast from 'components/application/UndoableToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import AssignPersonnelSidePanel from 'components/projectPersonnel/components/AssignPersonnelSidePanel';
import ProjectPersonnelBar from 'components/projectPersonnel/components/ProjectPersonnelBar';
import ProjectPersonnelDigitalSignaturesInfo from 'components/projectPersonnel/components/ProjectPersonnelDigitalSignaturesInfo';
import ProjectPersonnelTable from 'components/projectPersonnel/components/ProjectPersonnelTable';
import UnassignPersonnelModal from 'components/projectPersonnel/components/UnassignPersonnelModal';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';

export default function ProjectPersonnelTab() {
  const { addInedibleToast, addToast, handleBurnToast } = useBreadBoard();

  const {
    division: {
      attributes: { singularRAMSName },
    },
    isAllowedFeature,
  } = useCurrentActor();

  const { personnelPolicy, projectId, projectPersonnelPolicy, reviewPolicy } =
    useProjectPersonnelContext();

  const createProjectPersonnelBulkDestructionMutation =
    useCreateProjectPersonnelBulkDestructionMutation({ projectId });

  const createProjectPersonnelBulkRestorationMutation =
    useCreateProjectPersonnelBulkRestorationMutation({ projectId });

  const [currentPage, setCurrentPage] = useState(1);

  const [isAssignPersonnelSidePanelOpen, setIsAssignPersonnelSidePanelOpen] =
    useState(false);

  const [isUnassignPersonnelModalOpen, setIsUnassignPersonnelModalOpen] =
    useState(false);

  const [selectedProjectPersonnelIds, setSelectedProjectPersonnelIds] =
    useState(new Set());

  const {
    data: domainProjectPersonnel,
    isSuccess: isDomainProjectPersonnelSuccess,
  } = useProjectPersonnelsQuery({ params: { page: currentPage }, projectId });

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedProjectPersonnelIds(new Set());
  };

  const handleProjectPersonnelDestroySuccess = (projectPersonnelId) => {
    setSelectedProjectPersonnelIds((prevState) => {
      const newSelectedProjectPersonnelIds = new Set(prevState);
      newSelectedProjectPersonnelIds.delete(projectPersonnelId);
      return newSelectedProjectPersonnelIds;
    });
  };

  const handleProjectPersonnelBulkRemoveUndoClick = (projectPersonnelIds) => {
    createProjectPersonnelBulkRestorationMutation.mutate(
      { projectPersonnelIds: [...projectPersonnelIds] },
      {
        onSuccess: () => {
          addToast(
            <SuccessToast
              message={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='projectPersonnel.components.ProjectPersonnelTab.handleProjectPersonnelBulkRemoveUndoClick.success'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    count: projectPersonnelIds.size,
                    singularRAMSName,
                  }}
                />
              }
              onBurnToast={handleBurnToast}
            />,
          );
        },
        onError: addInedibleToast,
      },
    );
  };

  const handleProjectPersonnelBulkRemoveClick = (projectPersonnelIds) => {
    createProjectPersonnelBulkDestructionMutation.mutate(
      { projectPersonnelIds: [...projectPersonnelIds] },
      {
        onSuccess: () => {
          addToast(
            <UndoableToast
              onBurnToast={handleBurnToast}
              onUndoAction={() => {
                handleProjectPersonnelBulkRemoveUndoClick(projectPersonnelIds);
              }}
              toastText={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='projectPersonnel.shared.createProjectPersonnelBulkDestruction.success'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    count: projectPersonnelIds.size,
                    singularRAMSName,
                  }}
                />
              }
            />,
          );

          setIsUnassignPersonnelModalOpen(false);
          setSelectedProjectPersonnelIds(new Set());
        },
        onError: addInedibleToast,
      },
    );
  };

  const handleProjectPersonnelToggle = (projectPersonnelId) => {
    setSelectedProjectPersonnelIds((prevState) => {
      const newSelectedProjectPersonnelIds = new Set(prevState);

      if (prevState.has(projectPersonnelId)) {
        newSelectedProjectPersonnelIds.delete(projectPersonnelId);
      } else {
        newSelectedProjectPersonnelIds.add(projectPersonnelId);
      }

      return newSelectedProjectPersonnelIds;
    });
  };

  const handleSelectAllProjectPersonnelToggle = (projectPersonnelIds) => {
    setSelectedProjectPersonnelIds((prevState) =>
      prevState.size > 0 ? new Set() : new Set(projectPersonnelIds),
    );
  };

  const canDownloadTraining =
    personnelPolicy.canRead && isAllowedFeature('training_register');

  const isEmptyPage =
    isDomainProjectPersonnelSuccess && domainProjectPersonnel.data.length === 0;

  if (currentPage > 1 && isEmptyPage) handlePageChange(currentPage - 1);

  return (
    <>
      {reviewPolicy.canCreate && (
        <div className='tw-mb-6 tw-mt-4'>
          <ProjectPersonnelDigitalSignaturesInfo />
        </div>
      )}
      {(canDownloadTraining ||
        projectPersonnelPolicy.canCreate ||
        projectPersonnelPolicy.canDestroy) && (
        <ProjectPersonnelBar
          onAssignPersonnelClick={() => {
            setIsAssignPersonnelSidePanelOpen(true);
          }}
          onUnassignPersonnelClick={() => {
            setIsUnassignPersonnelModalOpen(true);
          }}
          selectedProjectPersonnelIds={selectedProjectPersonnelIds}
        />
      )}
      <ProjectPersonnelTable
        currentPage={currentPage}
        onAssignPersonnelClick={() => {
          setIsAssignPersonnelSidePanelOpen(true);
        }}
        onProjectPersonnelDestroySuccess={handleProjectPersonnelDestroySuccess}
        onProjectPersonnelToggle={handleProjectPersonnelToggle}
        onSelectAllProjectPersonnelToggle={
          handleSelectAllProjectPersonnelToggle
        }
        selectedProjectPersonnelIds={selectedProjectPersonnelIds}
      />
      {projectPersonnelPolicy.canCreate && (
        <AssignPersonnelSidePanel
          isOpen={isAssignPersonnelSidePanelOpen}
          onClose={() => setIsAssignPersonnelSidePanelOpen(false)}
        />
      )}
      {isDomainProjectPersonnelSuccess && (
        <>
          {domainProjectPersonnel.meta.totalPages > 1 && (
            <div className='tw-mt-20 tw-text-center'>
              <Paginator
                currentPage={domainProjectPersonnel.meta.currentPage}
                onClick={({ currentTarget }) =>
                  handlePageChange(
                    Number(currentTarget.getAttribute('data-page')),
                  )
                }
                totalPages={domainProjectPersonnel.meta.totalPages}
              />
            </div>
          )}
          {projectPersonnelPolicy.canDestroy &&
            isUnassignPersonnelModalOpen &&
            selectedProjectPersonnelIds.size > 0 && (
              <UnassignPersonnelModal
                domainProjectPersonnel={domainProjectPersonnel}
                isSubmitting={
                  createProjectPersonnelBulkDestructionMutation.isLoading
                }
                onClose={() => setIsUnassignPersonnelModalOpen(false)}
                onSubmitClick={handleProjectPersonnelBulkRemoveClick}
                projectPersonnelIds={selectedProjectPersonnelIds}
              />
            )}
        </>
      )}
    </>
  );
}
